/** Strategy and configuration registry: saved and active settings kept distinct (U071, U074; V08-A11). */
import { decodeRegistryRow, type ConfigRow, type RegistryRow } from "../../contracts/ops.ts";
import { badge, instantCell, metric } from "../../app/components.ts";
import { h, type Child } from "../../app/dom.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { byKindOf, mountOps, panel, simpleTable } from "../ops-common.ts";

const STATUS_TEXT: Readonly<Record<ConfigRow["status"], string>> = {
  IN_SYNC: "Saved and active agree",
  SAVED_NOT_ACTIVE: "Saved, not active",
  MISSING_BINDING: "Required binding missing",
};

function statusBadge(status: ConfigRow["status"]): HTMLElement {
  return badge(STATUS_TEXT[status], status === "IN_SYNC" ? "ok" : "bad");
}

const saved = (row: ConfigRow): Child[] => [
  `${row.savedValue} (version ${String(row.savedVersion)}) saved `,
  instantCell(row.savedAt),
];

const active = (row: ConfigRow): Child[] =>
  row.activeValue === null || row.activeVersion === null
    ? [metric(null), " not active"]
    : [
        `${row.activeValue} (version ${String(row.activeVersion)}) active since `,
        instantCell(row.activatedAt),
      ];

/** Draw the settings (saved beside active, never merged) and the activation history. */
export function drawRegistry(rows: readonly RegistryRow[]): readonly Child[] {
  const configs = byKindOf(rows, "config");
  const drift = configs.filter((row) => row.status !== "IN_SYNC");
  return [
    h(
      "p",
      drift.length === 0
        ? { attrs: { role: "status", "data-drift": "none" } }
        : { attrs: { role: "alert", "data-drift": String(drift.length) } },
      drift.length === 0
        ? "Every setting is saved and active at the same value."
        : `${String(drift.length)} setting(s) differ: a saved setting is not shown as deployed or active.`,
    ),
    h(
      "p",
      { class: "note" },
      "This page is read-only: a setting is changed through a reviewed change, never by editing a live value here.",
    ),
    panel(
      "Settings",
      "registry-settings",
      simpleTable(
        "Settings",
        ["Setting", "Meaning", "Depends on", "Saved", "Active", "Status"],
        configs.map((row) => [
          row.key,
          row.meaning,
          row.dependsOn.join(", "),
          h("span", { class: "saved", attrs: { "data-saved": row.key } }, ...saved(row)),
          h("span", { class: "active", attrs: { "data-active": row.key } }, ...active(row)),
          h("span", { attrs: { "data-status": row.status } }, statusBadge(row.status)),
        ]),
      ),
    ),
    panel(
      "Activation history",
      "registry-history",
      simpleTable(
        "Activation history",
        ["Release", "Component", "Activated", "Previous", "State", "Rollback reference"],
        byKindOf(rows, "activation").map((row) => [
          row.releaseId,
          row.component,
          instantCell(row.activatedAt),
          metric(row.previousRelease),
          row.active ? "active" : "superseded",
          metric(row.rollbackRef),
        ]),
      ),
    ),
  ];
}

/** Mount the registry. */
export function mountRegistry(ctx: ViewContext): ViewHandle {
  return mountOps(ctx, {
    id: "registry",
    title: "Strategy and configuration",
    projection: "registry",
    row: decodeRegistryRow,
    draw: (rows) => drawRegistry(rows),
  });
}
